import {Component, computed, inject, input, Signal} from '@angular/core';
import {Router} from '@angular/router';
import {NgTemplateOutlet} from '@angular/common';
import {animate, style, transition, trigger} from '@angular/animations';

import {CubesManagerModule} from '@studio2wx/cubes-manager';
import {DataBatchFacade} from '@selpia/data-batch/data-access';
import {AppConfigFacade} from '@selpia/app-config/data-access';
import {
  DirectionIndicatorComponent,
  DrilldownComponent,
  DrilldownTarget,
  FeaturedValueComponent,
  L12mBarChartComponent,
  ReportButtonComponent,
  TileHeaderComponent,
} from '@selpia/data-batch/ui';
import {TileLoaderComponent} from '@selpia/shared/ui';
import {OptionsFacade} from '@selpia/options/data-access';
import {NavigationHelper} from '@selpia/data-batch/history-access';
import {SelectDataPointPipe} from '@selpia/data-batch/ui';
import {DataChunk, Measure, Perspective, Sentiment} from '@selpia/shared/models';

@Component({
  selector: 'lib-feature-kpi-grid',
  templateUrl: './feature-kpi-grid.component.html',
  styleUrl: './feature-kpi-grid.component.scss',
  imports: [
    CubesManagerModule,
    DirectionIndicatorComponent,
    FeaturedValueComponent,
    TileHeaderComponent,
    SelectDataPointPipe,
    L12mBarChartComponent,
    NgTemplateOutlet,
    DrilldownComponent,
    TileLoaderComponent,
    ReportButtonComponent,
  ],
  animations: [
    trigger('fadeOut', [
      transition(':leave', [
        animate(
          '600ms cubic-bezier(0.4, 0.0, 1, 1)',
          style({opacity: 0}),
        ),
      ]),
    ]),
  ],
})
export class FeatureKpiGridComponent {
  readonly #dataBatchFacade = inject(DataBatchFacade);
  readonly #appConfigFacade = inject(AppConfigFacade);
  readonly #optionsFacade = inject(OptionsFacade);

  readonly #router: Router = inject(Router);

  readonly tileViewPage = input.required<string>();
  readonly reportPath = input<string>();
  readonly topPerspective = input<Perspective>();

  protected readonly isLoading: Signal<boolean> = computed(() => this.#dataBatchFacade.isLoading() || !this.dataChunk()?.dataPoints.length || this.#appConfigFacade.isLoading());

  protected readonly dataChunk: Signal<DataChunk | undefined> = computed(() => this.#dataBatchFacade.dataChunks()
    ?.at(0));

  protected readonly growthMeasureLabel: Signal<string | undefined> = this.#appConfigFacade.growthMeasureLabel;
  protected readonly focusMeasures: Signal<Measure[]> = this.#appConfigFacade.focusMeasures;
  protected readonly date: Signal<Date | undefined> = this.#appConfigFacade.initialDate;

  protected readonly periodCode: Signal<string | undefined> = this.#optionsFacade.focusPeriod;

  handleDrilldown(target: DrilldownTarget, measure: Measure) {
    const perspective: Partial<Perspective> = {
      focusDimensionLevel: target.targetLevel,
      filterDimensions: target.dimensionItems,
      date: this.date(),
    };

    const options = {focusMeasure: measure, focusPeriod: this.periodCode()};

    const queryParams = {
      ...NavigationHelper.getParamsForPerspective(perspective),
      ...NavigationHelper.getParamsForOptions(options),
    };

    this.#router.navigate([`/${this.tileViewPage()}`], {queryParams})
      .then();
  }

  protected readonly Sentiment = Sentiment;
}
